import { moduleApi } from "@amtana/module";
import type { EraseStep } from "@amtana/module";

import type { EnrichableField, ProspectScope } from "./prospect-conditions.schemas.ts";
import type {
  ChangedCount,
  CountMatching,
  EnrichmentSelection,
  IngestInput,
  IngestOutcome,
  ListProspectsInput,
  Prospect,
  ProspectCounts,
  ProspectPage,
  RemoveProspectsInput,
  ScoreOutput,
  UpdateProspectsInput,
} from "./prospect.schemas.ts";

/**
 * Every call is scoped to the caller's active organization (ADR-001 §5), the
 * pool prospect shares across every project of that organization (S2.05);
 * `userId` is who is acting, kept only as the author of what they touch, never
 * as the boundary.
 */
type ForOrganization = { readonly userId: string; readonly organizationId: string };

export interface ProspectApi extends EraseStep {
  list(input: ForOrganization & ListProspectsInput): Promise<ProspectPage>;
  get(input: ForOrganization & { readonly prospectId: string }): Promise<Prospect>;
  /** The seeded release flag's own demo door (S8.03): a whole procedure that does not exist
   * while "lead-scoring" is off for the caller's organization. */
  score(input: ForOrganization & { readonly prospectId: string }): Promise<ScoreOutput>;
  update(input: ForOrganization & UpdateProspectsInput): Promise<ChangedCount>;
  remove(input: ForOrganization & RemoveProspectsInput): Promise<ChangedCount>;
  /** Saves the public profiles found in [from, to) of an analysis's audience. */
  ingest(input: IngestInput): Promise<IngestOutcome>;
  countsFor(input: ForOrganization & { readonly analysisId: string }): Promise<ProspectCounts>;
  /** How many prospects a scope holds (a filter's size). */
  /** Settings' purge: every prospect of the organization, gone. */
  purge(input: ForOrganization): Promise<ChangedCount>;
  countMatching(input: ForOrganization & ProspectScope): Promise<CountMatching>;
  /** Every prospect in a scope, in list order, up to `limit` (exports read through this). */
  inScope(input: ForOrganization & ProspectScope & { readonly limit: number }): Promise<readonly Prospect[]>;
  /** The public prospects in a scope that still miss any of the fields (all of them when `reEnrich`). */
  selectForEnrichment(
    input: ForOrganization &
      ProspectScope & { readonly fields: readonly EnrichableField[]; readonly reEnrich: boolean },
  ): Promise<EnrichmentSelection>;
  /** Fills the fields on these prospects from their source data. */
  applyEnrichment(
    input: ForOrganization & {
      readonly prospectIds: readonly string[];
      readonly fields: readonly EnrichableField[];
    },
  ): Promise<ChangedCount>;
  /** Unlinks an analysis; a prospect left with no analysis is deleted. */
  removeForAnalysis(input: ForOrganization & { readonly analysisId: string }): Promise<ChangedCount>;
  /** Operator-only (D053): no transport door. Stamps rows left over from before S1.03. */
  backfillOrganizationId(): Promise<{ readonly assigned: number }>;
}

export const ProspectApi = moduleApi<ProspectApi>()("prospect");
